import { useEffect, useState } from "react";
import type { Projects as ProjectType } from "@repo/fetch-repository/projects";
import VideoPlayer from "../../common/video-player";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "../../ui/carousel";
import { Progress } from "../../ui/progress";

interface ProjectDetailProps {
  projectDetails: ProjectType;
  locale: string;
  projectDetailLabels: {
    name: string;
    date: string;
    customer: string;
    progressRate: string;
    industrialProcess: string;
    projectType: string;
    region: string;
    rectifierTechnology: string;
    min: string;
    max: string;
  };
}

export function ProjectDetail(props: ProjectDetailProps): React.JSX.Element {
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);
  const [thumbnailApi, setThumbnailApi] = useState<CarouselApi | null>(null);

  useEffect(() => {
    if (!mainApi) {
      return;
    }

    mainApi.on("select", () => {
      const index = mainApi.selectedScrollSnap();
      thumbnailApi?.scrollTo(index);
    });
  }, [mainApi, thumbnailApi]);
  return (
    <div className="w-full h-full grid grid-cols-1 lg:grid-cols-2 lg:gap-x-10 gap-y-6 relative z-10">
      <div className="w-full h-full flex flex-col justify-start items-start gap-y-3 md:gap-y-4 lg:gap-y-7 text-base lg:text-xl leading-[1.6] text-[#4c4c4c] font-light font-helvetica rtl:font-doran rtl:font-bold">
        <div className="flex flex-col justify-start items-start gap-y-1">
          <p className="font-semibold">{props.projectDetailLabels.name}</p>
          <p className="font-[100] rtl:font-medium">
            {props.projectDetails.name}
          </p>
        </div>
        <div className="flex flex-col justify-start items-start gap-y-1">
          <p className="font-semibold">{props.projectDetailLabels.date}</p>
          <p className="font-[100] rtl:font-medium">
            {props.projectDetails.year}
          </p>
        </div>
        {!Array.isArray(props.projectDetails.customer.data) ? (
          <div className="flex flex-col justify-start items-start gap-y-1">
            <p className="font-semibold">
              {props.projectDetailLabels.customer}
            </p>
            <p className="font-[100] rtl:font-medium">
              {props.projectDetails.customer.data.name}
            </p>
          </div>
        ) : null}
        {!Array.isArray(props.projectDetails.region.data) ? (
          <div className="flex flex-col justify-start items-start gap-y-1">
            <p className="font-semibold">{props.projectDetailLabels.region}</p>
            <p className="font-[100] rtl:font-medium">
              {props.projectDetails.region.data.name}
            </p>
          </div>
        ) : null}
        {!Array.isArray(props.projectDetails.project_type.data) ? (
          <div className="flex flex-col justify-start items-start gap-y-1">
            <p className="font-semibold">
              {props.projectDetailLabels.projectType}
            </p>
            <p className="font-[100] rtl:font-medium">
              {props.projectDetails.project_type.data.name}
            </p>
          </div>
        ) : null}
        {!Array.isArray(props.projectDetails.rectifier_technology.data) ? (
          <div className="flex flex-col justify-start items-start gap-y-1">
            <p className="font-semibold">
              {props.projectDetailLabels.rectifierTechnology}
            </p>
            <p className="font-[100] rtl:font-medium">
              {props.projectDetails.rectifier_technology.data.name}
            </p>
          </div>
        ) : null}
        {!Array.isArray(props.projectDetails.industrial_process.data) ? (
          <div className="flex flex-col justify-start items-start gap-y-1">
            <p className="font-semibold">
              {props.projectDetailLabels.industrialProcess}
            </p>
            <p className="font-[100] rtl:font-medium">
              {props.projectDetails.industrial_process.data.name}
            </p>
          </div>
        ) : null}
        <div className="flex flex-col justify-start items-start gap-y-1 w-full">
          <p className="pb-4 font-semibold">{props.projectDetailLabels.min}</p>
          <p className="font-[100] rtl:font-medium">
            {props.projectDetails.min_current}
          </p>
        </div>
        <div className="flex flex-col justify-start items-start gap-y-1 w-full">
          <p className="pb-4 font-semibold">{props.projectDetailLabels.max}</p>
          <p className="font-[100] rtl:font-medium">
            {props.projectDetails.max_current}
          </p>
        </div>
        <div className="flex flex-col justify-start items-start gap-y-1 w-full">
          <p className="pb-4 font-semibold">
            {props.projectDetailLabels.progressRate}
          </p>
          <Progress
            className="w-full"
            percent
            value={props.projectDetails.progress_rate}
          />
        </div>
        <div
          className="pt-8 text-sm lg:text-lg leading-[1.5] text-[#4c4c4c] font-light font-helvetica"
          dangerouslySetInnerHTML={{ __html: props.projectDetails.summery }}
        />
      </div>
      <div
        className="w-full h-full flex flex-col justify-start items-start gap-y-7"
        dir="ltr"
      >
        <Carousel className="w-full" setApi={setMainApi}>
          <CarouselContent className="w-full">
            {!Array.isArray(props.projectDetails.main_image.data) ? (
              <CarouselItem className="basis-full">
                {props.projectDetails.main_image.data.url.includes("mp4") ? (
                  <VideoPlayer
                    link={props.projectDetails.main_image.data.url || ""}
                  />
                ) : (
                  <div className="col-span-1 lg:col-span-2">
                    <img
                      alt=""
                      className="w-full lg:h-[360px] h-[227px] object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "/images/placeholder.png";
                      }}
                      src={
                        props.projectDetails.main_image.data.url ||
                        "/images/placeholder.png"
                      }
                    />
                  </div>
                )}
              </CarouselItem>
            ) : null}
            {props.projectDetails.gallery_images.data.length
              ? props.projectDetails.gallery_images.data.map((image, index) => (
                  <CarouselItem className="basis-full" key={index}>
                    <img
                      alt={image.alt || props.projectDetails.name}
                      className="w-full lg:h-[360px] h-[227px] object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "/images/placeholder.png";
                      }}
                      src={image.url}
                    />
                  </CarouselItem>
                ))
              : null}
          </CarouselContent>
        </Carousel>
        <div className="flex gap-2 w-full">
          <Carousel setApi={setThumbnailApi}>
            <CarouselContent>
              {!Array.isArray(props.projectDetails.main_image.data) ? (
                <CarouselItem className="lg:basis-1/4 md:basis-1/3 basis-full">
                  <button
                    className="w-full h-[66.4px] md:h-[100px]"
                    onClick={() => {
                      mainApi?.scrollTo(0);
                    }}
                    type="button"
                  >
                    {props.projectDetails.main_image.data.url.includes(
                      "mp4"
                    ) ? (
                      <VideoPlayer
                        link={props.projectDetails.main_image.data.url || ""}
                      />
                    ) : (
                      <div className="col-span-1 lg:col-span-2">
                        <img
                          alt=""
                          className="object-cover object-center hover:opacity-80 transition-opacity h-full"
                          onError={(e) => {
                            e.currentTarget.src = "/images/placeholder.png";
                          }}
                          src={
                            props.projectDetails.main_image.data.url ||
                            "/images/placeholder.png"
                          }
                        />
                      </div>
                    )}
                  </button>
                </CarouselItem>
              ) : null}
              {props.projectDetails.gallery_images.data.length
                ? props.projectDetails.gallery_images.data.map(
                    (image, index) => (
                      <CarouselItem
                        className="lg:basis-1/4 md:basis-1/3 basis-full"
                        key={image.id}
                      >
                        <button
                          className="w-full h-[66.4px] md:h-[100px]"
                          onClick={() => {
                            mainApi?.scrollTo(
                              index +
                                (!Array.isArray(
                                  props.projectDetails.main_image.data
                                )
                                  ? 1
                                  : 0)
                            );
                          }}
                          type="button"
                        >
                          <img
                            alt={`${image.alt || props.projectDetails.name} thumbnail ${image.id}`}
                            className="object-cover object-center hover:opacity-80 transition-opacity"
                            onError={(e) => {
                              e.currentTarget.src = "/images/placeholder.png";
                            }}
                            src={image.url}
                          />
                        </button>
                      </CarouselItem>
                    )
                  )
                : null}
            </CarouselContent>
          </Carousel>
        </div>
        {!Array.isArray(props.projectDetails.video.data) ? (
          <div className="rounded-sm lg:h-72 h-40">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- disable */}
            <video
              autoPlay
              controls
              loop
              src={props.projectDetails.video.data.url}
            />
          </div>
        ) : null}
      </div>
      <div
        className="pt-8 text-sm lg:text-lg leading-[1.5] text-[#4c4c4c] font-light font-helvetica rtl:font-doran"
        dangerouslySetInnerHTML={{ __html: props.projectDetails.detail }}
      />
    </div>
  );
}
